// Utils
import {
    getSecondaryColor,
    getElementId,
    getColor as getColorFromState,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getChildren } from '../../../common/elements/utils/elementTraversalUtils';
import { removeDuplicateObjects } from '../../../common/utils/arrayUtils';
import { MAX_PALETTE_SIZE } from '../../../common/colors/colorComboUtils';
import { isAlias, isBoard, isColumn } from '../../../common/elements/elementHelper';

// Selectors
import { generateColorComboPalette, getCurrentBoardUsedColorCombos } from './boardSelector';
import { getElements } from '../selectors/elementsSelector';

/**
 * Check for if the current selected element(s) should display the color combos or not.
 * Currently only allowing for boards and aliases but in the future might extend to more elements like cards
 * @param {*} element
 * @returns boolean
 */
export const elementAllowsColorCombos = (element) => isBoard(element) || isAlias(element);

/**
 * Takes the list of suggested colors and the list of color combos, and combines
 * them in a way that results in a more even spread of colors, avoiding similar ones together.
 * This list is used to pick from when creating new boards.
 * Example: suggested = [A0, A1, A2, A3, A4, A5, A6], colorCombos = [B0, B1, B2, B3, B4, B5, B6]
 * If we were to simply alternate between the two we would end up with [A0, B0, A1, B1 ...] but in this case the colors
 * A0 and B0 tend to be the same/similar so we want to offset one of the lists. For this reason we are slicing the
 * suggested colors and starting from index 4 so suggested becomes [A4, A5, A6, A0, A1, A2, A3],
 * which results in a combined list = [B0, A4, B1, A5, B2, A6, B3, A0, B4, A1, B5, A2, B6, A3]
 */
export const combineLists = (suggested, colorCombos) => {
    const combined = [];
    // start the array at index 4, add the beginning items to the end
    const suggestedSliced = [...suggested.slice(4, suggested.length), ...suggested.slice(0, 4)];
    // add suggested and colorCombos, alternating between the two lists
    const length = Math.min(suggested.length, colorCombos.length);
    for (let index = 0; index < length; index++) {
        combined.push(colorCombos[index], suggestedSliced[index]);
    }
    // add any leftovers (whichever list was longer) to the combined array
    combined.push(...suggestedSliced.slice(length), ...colorCombos.slice(length));
    return combined;
};

/**
 * Check all the content on the current board and find all the color combo pairs currently in use
 * @param {*} getState
 * @param {*} id
 * @returns an array of unique color pairs that are currently in use
 */
export const getUsedColorCombos = (getState, id) => {
    const state = getState();
    const colorCombos = [];
    const elements = getElements(state);
    const children = getChildren(elements, id);
    // get all the used color colorCombos from child boards and aliases
    children.forEach((child) => {
        const childId = getElementId(child);
        if (elementAllowsColorCombos(child) && getSecondaryColor(child)) {
            colorCombos.push({ primary: getColorFromState(child), secondary: getSecondaryColor(child) });
        }

        // get all used colorCombos from boards within columns
        if (isColumn(child)) {
            const grandChildren = getChildren(elements, childId);
            grandChildren.forEach((grandChild) => {
                if (elementAllowsColorCombos(grandChild) && getSecondaryColor(grandChild)) {
                    colorCombos.push({
                        primary: getColorFromState(grandChild),
                        secondary: getSecondaryColor(grandChild),
                    });
                }
            });
        }
    });
    // Make entries unique objects with Set
    return removeDuplicateObjects(colorCombos);
};

/**
 * Get the used colorCombos and the generated colorCombo list, combine them and cut down to max number of colorCombos if necessary
 * @returns list to be displayed in the palette
 */
export const combineUsedAndGeneratedColorCombos = (getState) => {
    const state = getState();
    const colorComboList = generateColorComboPalette(state) ?? [];
    const usedColorCombos = getCurrentBoardUsedColorCombos(state) ?? [];
    const combinedList = removeDuplicateObjects([...colorComboList, ...usedColorCombos]);
    // if the combined array is greater than the maximum allowed number of colorCombos then remove
    // from the front. This will result in keeping as many already used colorCombos available as possible
    return combinedList.slice(Math.max(combinedList.length - MAX_PALETTE_SIZE, 0));
};
